import React from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@heroui/react';
import { Widget } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

const PLACEHOLDER_COUNT = 10;

export const PopularBrandsSkeleton: React.FC = () => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            title: t('sales.widgets.popularBrands.title'),
        }),
        [t],
    );

    return (
        <section
            aria-busy
            aria-labelledby="popular-brands-widget-title"
            {...getNodeTestIdProp('popular-brands-section')}
        >
            <Widget
                className="flex flex-col gap-4"
                {...getNodeTestIdProp('popular-brands-widget')}
            >
                <Widget.Header
                    className="mt-4"
                    {...getNodeTestIdProp('popular-brands-widget-header')}
                >
                    <Widget.Title
                        className="flex items-center gap-2"
                        {...getNodeTestIdProp('popular-brands-widget-title')}
                    >
                        <Skeleton
                            className="size-8 shrink-0 rounded-lg"
                            {...getNodeTestIdProp(
                                'popular-brands-icon-skeleton',
                            )}
                        />
                        {copy.title}
                    </Widget.Title>
                    <Widget.Description
                        {...getNodeTestIdProp('popular-brands-widget-subtitle')}
                    >
                        <Skeleton
                            className="h-3 w-32 rounded-md"
                            {...getNodeTestIdProp(
                                'popular-brands-widget-subtitle-skeleton',
                            )}
                        />
                    </Widget.Description>
                </Widget.Header>
                <Widget.Content className="flex flex-col gap-6 sm:flex-row sm:items-start">
                    <Skeleton
                        className="mx-auto size-47.5 shrink-0 rounded-full sm:mx-0"
                        {...getNodeTestIdProp('popular-brands-chart-skeleton')}
                    />
                    <ul
                        aria-label={copy.title}
                        className="-mr-2 flex w-full min-w-0 flex-col gap-2 overflow-y-auto rounded-md pr-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus sm:max-h-47.5 sm:flex-1"
                        tabIndex={0}
                        {...getNodeTestIdProp('popular-brands-legend')}
                    >
                        {Array.from({ length: PLACEHOLDER_COUNT }).map(
                            (_, index) => {
                                const discriminator = `placeholder-${index}`;
                                return (
                                    <li
                                        key={discriminator}
                                        className="flex items-center justify-between gap-2 py-0.5"
                                        {...getNodeTestIdProp(
                                            `popular-brands-item-${discriminator}`,
                                        )}
                                    >
                                        <div
                                            className="flex min-w-0 items-center gap-2"
                                            {...getNodeTestIdProp(
                                                `popular-brands-item-label-root-${discriminator}`,
                                            )}
                                        >
                                            <Skeleton
                                                className="size-2.5 shrink-0 rounded-sm"
                                                {...getNodeTestIdProp(
                                                    `popular-brands-item-swatch-${discriminator}`,
                                                )}
                                            />
                                            <Skeleton
                                                className="h-3 w-32 rounded-md"
                                                {...getNodeTestIdProp(
                                                    `popular-brands-item-name-${discriminator}`,
                                                )}
                                            />
                                        </div>
                                        <div
                                            className="flex shrink-0 items-center gap-2"
                                            {...getNodeTestIdProp(
                                                `popular-brands-item-meta-root-${discriminator}`,
                                            )}
                                        >
                                            <Skeleton
                                                className="h-3 w-14 rounded-md"
                                                {...getNodeTestIdProp(
                                                    `popular-brands-item-revenue-${discriminator}`,
                                                )}
                                            />
                                            <Skeleton
                                                className="h-2.5 w-8 rounded-md"
                                                {...getNodeTestIdProp(
                                                    `popular-brands-item-share-${discriminator}`,
                                                )}
                                            />
                                        </div>
                                    </li>
                                );
                            },
                        )}
                    </ul>
                </Widget.Content>
            </Widget>
        </section>
    );
};
